import { useEffect, useMemo, useState } from 'react';

import { Stack } from 'ui/stack';

import { useFeatureFlags } from 'components/shared/feature-flags';

import { FeatureHintsProvider } from 'components/shared/feature-hints';
import type { FeatureHintStep } from 'components/shared/feature-hints';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { MarkSurfaceProvider } from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/mark-surface-context';

import FiltersBar from 'containers/user/critical-elements-monitoring/components/filters-bar';
import MonitoringEmptyState from 'containers/user/critical-elements-monitoring/components/monitoring-empty-state';
import TotalFunnelSection from 'containers/user/critical-elements-monitoring/components/total-funnel-section';
import TotalFunnelSkeleton from 'containers/user/critical-elements-monitoring/components/total-funnel-skeleton';
import CriticalElementsRoster from 'containers/user/critical-elements-monitoring/components/critical-elements-roster';
import MonitoringTourStartTrigger from 'containers/user/critical-elements-monitoring/components/monitoring-tour-start-trigger';
import { useMonitoringFilterOptions } from 'containers/user/critical-elements-monitoring/hooks/use-monitoring-filter-options';
import { useMonitoringCriticalElements } from 'containers/user/critical-elements-monitoring/hooks/use-monitoring-critical-elements';
import { useMonitoringDashboardMetrics } from 'containers/user/critical-elements-monitoring/hooks/use-monitoring-dashboard-metrics';
import { useClientMonitoringProducts } from 'containers/user/critical-elements-monitoring/hooks/use-client-monitoring-products';
import { useMonitoringErrorToast } from 'containers/user/critical-elements-monitoring/hooks/use-monitoring-error-toast';
import { getProspectAvailability } from 'containers/user/critical-elements-monitoring/helpers/get-prospect-availability';
import {
  FUNNEL_STAGE_COUNT,
  MONITORING_ERROR_TEXT,
  MONITORING_ERROR_TOAST_TITLE,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import {
  trackMonitoringCompareToToggled,
  trackMonitoringDisplayModeChanged,
  trackMonitoringDisplayOptionChanged,
  trackMonitoringExtendWithToggled,
} from 'containers/user/critical-elements-monitoring/analytics/monitoring-analytics';
import {
  trackMonitoringWalkthroughCompleted,
  trackMonitoringWalkthroughDismissed,
  trackMonitoringWalkthroughViewed,
} from 'containers/user/critical-elements-monitoring/analytics/monitoring-walkthrough-analytics';
import {
  MONITORING_HINTS_STEPS,
  MONITORING_START_HINTS_EVENT,
} from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-hints.config';
import {
  hasMonitoringMarkData,
  resolveMonitoringMarkContext,
} from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-context';
import type { MonitoringMarkSnapshot } from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-context';
import { buildMonitoringPageContext } from 'containers/user/critical-elements-monitoring/mark/monitoring-page-context';
import type {
  FunnelDisplayMode,
  StageCardDisplayMode,
} from 'containers/user/critical-elements-monitoring/components/funnel-stage-card';
import type {
  CompareTo,
  ExtendWithOption,
  MonitoringFilterOptions,
  MonitoringShowToggles,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

const DEFAULT_ENTERING_CLASS = 2027;

const NO_COHORTS_TEXT = 'No cohorts configured yet';
const NO_ENTERING_CLASSES_TEXT = 'No entering classes configured yet';
const NO_MONITORING_DATA_TEXT = 'No monitoring data available';

const getEmptyFilterOptionsText = (
  filterOptions: MonitoringFilterOptions
): string =>
  filterOptions.enteringClasses.length
    ? NO_COHORTS_TEXT
    : NO_ENTERING_CLASSES_TEXT;

const DEFAULT_SHOW_TOGGLES: MonitoringShowToggles = {
  client: true,
  cohort: true,
  vsCohort: false,
};

export interface MonitoringDashboardProps {
  clientId: string;
  isAnalyticsEnabled?: boolean;
  isMarkEnabled?: boolean;
  isActive?: boolean;
  onInitialLoadSettled?: () => void;
}

const MonitoringDashboard = ({
  clientId,
  isAnalyticsEnabled = true,
  isMarkEnabled = true,
  isActive = true,
  onInitialLoadSettled,
}: MonitoringDashboardProps): React.JSX.Element => {
  const { featureFlags } = useFeatureFlags();
  const currentUser = useCurrentUser();
  const oktaUserId = currentUser.data?.oktaUserId;
  const filterOptionsQuery = useMonitoringFilterOptions(clientId);
  const {
    entitledExtendWithOptions,
    loading: productsLoading,
    error: extendOptionsError,
    refetch: refetchExtendOptions,
  } = useClientMonitoringProducts(clientId);

  const [enteringClass, setEnteringClass] = useState<number | null>(null);
  const criticalElementsQuery = useMonitoringCriticalElements(
    clientId,
    enteringClass
  );
  const [compareTo, setCompareTo] = useState<CompareTo>('point-in-time');
  const [cohort, setCohort] = useState<string | null>(null);
  const [extend, setExtend] = useState<ExtendWithOption[]>([]);
  const [show, setShow] = useState<MonitoringShowToggles>(DEFAULT_SHOW_TOGGLES);
  const [displayMode, setDisplayMode] = useState<FunnelDisplayMode>('volume');
  const [ceDisplayMode, setCeDisplayMode] =
    useState<StageCardDisplayMode>('volume');
  const [hasContentLoaded, setHasContentLoaded] = useState(false);

  useEffect(() => {
    if (
      enteringClass === null &&
      filterOptionsQuery.data?.enteringClasses.length
    ) {
      const { enteringClasses } = filterOptionsQuery.data;

      setEnteringClass(
        enteringClasses.includes(DEFAULT_ENTERING_CLASS)
          ? DEFAULT_ENTERING_CLASS
          : Math.max(...enteringClasses)
      );
    }
  }, [enteringClass, filterOptionsQuery.data]);

  useEffect(() => {
    if (cohort === null && filterOptionsQuery.data?.cohorts.length) {
      const [firstCohort] = filterOptionsQuery.data.cohorts;

      setCohort(firstCohort.key);
    }
  }, [cohort, filterOptionsQuery.data]);

  const productsResolved = !productsLoading && !extendOptionsError;

  const selectedUnentitledExtensions = useMemo(
    () =>
      productsResolved
        ? extend.filter((option) => !entitledExtendWithOptions.includes(option))
        : [],
    [extend, entitledExtendWithOptions, productsResolved]
  );

  const entitledExtend = useMemo(
    () =>
      productsResolved
        ? extend.filter((option) => entitledExtendWithOptions.includes(option))
        : extend,
    [extend, entitledExtendWithOptions, productsResolved]
  );

  const prospectAvailability = getProspectAvailability(
    productsResolved && entitledExtend.includes('prospect'),
    selectedUnentitledExtensions
  );

  const metricsQuery = useMonitoringDashboardMetrics({
    clientId,
    enteringClass,
    monitoringYear: enteringClass,
    compareTo,
    cohort,
    extend: entitledExtend,
    show,
  });

  const skeletonCardCount = FUNNEL_STAGE_COUNT + extend.length;
  const effectiveDisplayMode: FunnelDisplayMode =
    compareTo === 'prior-year' ? 'volume' : displayMode;
  const effectiveCeDisplayMode: StageCardDisplayMode =
    compareTo === 'prior-year' ? 'volume' : ceDisplayMode;

  const handleEnteringClassChange = (nextEnteringClass: number): void => {
    if (nextEnteringClass === enteringClass) return;

    if (isAnalyticsEnabled) {
      trackMonitoringDisplayOptionChanged({
        displayOption: 'enteringClass',
        value: nextEnteringClass,
      });
    }
    setEnteringClass(nextEnteringClass);
  };

  const handleCompareToChange = (nextCompareTo: CompareTo): void => {
    if (nextCompareTo === compareTo) return;

    if (isAnalyticsEnabled) {
      trackMonitoringDisplayOptionChanged({
        displayOption: 'changeInTime',
        value: nextCompareTo,
      });
    }
    setCompareTo(nextCompareTo);
  };

  const handleCohortChange = (nextCohort: string): void => {
    if (nextCohort === cohort) return;

    if (isAnalyticsEnabled) {
      trackMonitoringDisplayOptionChanged({
        displayOption: 'cohort',
        value: nextCohort,
      });
    }
    setCohort(nextCohort);
  };

  const handleDisplayModeChange = (
    nextDisplayMode: FunnelDisplayMode
  ): void => {
    if (nextDisplayMode === effectiveDisplayMode) return;

    if (isAnalyticsEnabled) {
      trackMonitoringDisplayModeChanged({
        section: 'totalFunnel',
        displayMode: nextDisplayMode,
      });
    }
    setDisplayMode(nextDisplayMode);
  };

  const handleCeDisplayModeChange = (
    nextDisplayMode: StageCardDisplayMode
  ): void => {
    if (nextDisplayMode === effectiveCeDisplayMode) return;

    if (isAnalyticsEnabled) {
      trackMonitoringDisplayModeChanged({
        section: 'criticalElements',
        displayMode: nextDisplayMode,
      });
    }
    setCeDisplayMode(nextDisplayMode);
  };

  const handleExtendToggle = (option: ExtendWithOption): void => {
    if (isAnalyticsEnabled) {
      trackMonitoringExtendWithToggled({
        option,
        action: extend.includes(option) ? 'deselected' : 'selected',
      });
    }
    setExtend((current) =>
      current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option]
    );
  };

  const handleShowToggle = (
    key: keyof MonitoringShowToggles,
    value: boolean
  ): void => {
    if (isAnalyticsEnabled) {
      trackMonitoringCompareToToggled({
        showToggle: key,
        action: value ? 'selected' : 'deselected',
      });
    }
    setShow((current) => {
      const next = { ...current, [key]: value };

      if (!(next.client && next.cohort)) {
        next.vsCohort = false;
      }

      return next;
    });
  };

  const cohortLabel =
    filterOptionsQuery.data?.cohorts.find((option) => option.key === cohort)
      ?.label ??
    cohort ??
    '';

  const snapshot: MonitoringMarkSnapshot = useMemo(
    () => ({
      enteringClass: enteringClass ?? 0,
      compareTo,
      cohortLabel,
      displayMode: effectiveDisplayMode,
      ceDisplayMode: effectiveCeDisplayMode,
      extend: entitledExtend,
      metrics: metricsQuery.data,
      criticalElements: criticalElementsQuery.data ?? [],
    }),
    [
      enteringClass,
      compareTo,
      cohortLabel,
      effectiveDisplayMode,
      effectiveCeDisplayMode,
      entitledExtend,
      metricsQuery.data,
      criticalElementsQuery.data,
    ]
  );

  const filterOptions = filterOptionsQuery.data;

  const hasEmptyFilterOptions =
    !!filterOptions &&
    (!filterOptions.enteringClasses.length || !filterOptions.cohorts.length);

  const isDashboardContentVisible =
    !hasEmptyFilterOptions &&
    !!clientId &&
    !filterOptionsQuery.isLoading &&
    !!filterOptions &&
    enteringClass !== null &&
    cohort !== null;

  const isTourReady =
    isDashboardContentVisible &&
    !metricsQuery.isLoading &&
    !criticalElementsQuery.isLoading &&
    !productsLoading;

  useEffect(() => {
    if (isTourReady) setHasContentLoaded(true);
  }, [isTourReady]);

  const isMarkDataAbsent =
    !metricsQuery.isFetching && !hasMonitoringMarkData(metricsQuery.data);

  const isMonitoringDataEmpty =
    !!metricsQuery.data &&
    !metricsQuery.isError &&
    !hasMonitoringMarkData(metricsQuery.data);

  const isMetricsUnavailable = metricsQuery.isError || isMonitoringDataEmpty;

  useMonitoringErrorToast(
    filterOptionsQuery.error,
    MONITORING_ERROR_TOAST_TITLE.filterOptions
  );
  useMonitoringErrorToast(
    metricsQuery.error,
    MONITORING_ERROR_TOAST_TITLE.totalFunnel
  );
  useMonitoringErrorToast(
    criticalElementsQuery.error,
    MONITORING_ERROR_TOAST_TITLE.criticalElements
  );

  const isInitialLoadSettled =
    isTourReady || filterOptionsQuery.isError || hasEmptyFilterOptions;

  useEffect(() => {
    if (isInitialLoadSettled) onInitialLoadSettled?.();
  }, [isInitialLoadSettled, onInitialLoadSettled]);

  if (filterOptionsQuery.isError) {
    return <MonitoringEmptyState isError text={MONITORING_ERROR_TEXT} />;
  }

  return (
    <MarkSurfaceProvider
      surfaceId="monitoring"
      clientId={clientId}
      isEnabled={
        isMarkEnabled &&
        isActive &&
        featureFlags['ai-mark-monitoring'] &&
        hasContentLoaded &&
        !isMarkDataAbsent
      }
      isAnalyticsEnabled={isAnalyticsEnabled}
      snapshot={snapshot}
      resolveContext={resolveMonitoringMarkContext}
      buildPageContext={buildMonitoringPageContext}
    >
      <FeatureHintsProvider
        steps={MONITORING_HINTS_STEPS}
        skipMissingTargets
        startEventName={MONITORING_START_HINTS_EVENT}
        onStepViewed={(step: FeatureHintStep, configuredIndex: number) => {
          if (!isAnalyticsEnabled) return;

          trackMonitoringWalkthroughViewed({
            section: 'hintTour',
            stepNumber: configuredIndex + 1,
            stepId: step.id,
          });
        }}
        onTourDismissed={(
          _step: FeatureHintStep,
          configuredIndex: number,
          reason: 'skip' | 'escape'
        ) => {
          if (!isAnalyticsEnabled) return;

          trackMonitoringWalkthroughDismissed({
            section: 'hintTour',
            dismissMethod: reason,
            stepNumber: configuredIndex + 1,
          });
        }}
        onTourCompleted={() => {
          if (!isAnalyticsEnabled) return;

          trackMonitoringWalkthroughCompleted({
            section: 'hintTour',
            completionType: 'hintsFinished',
          });
        }}
      >
        <MonitoringTourStartTrigger
          isReady={isTourReady && hasContentLoaded && !isMetricsUnavailable}
          isActive={isActive}
          isAnalyticsEnabled={isAnalyticsEnabled}
          oktaUserId={oktaUserId}
        />

        <Stack sx={{ gap: 2, flex: '1 1 0%' }}>
          {hasEmptyFilterOptions ? (
            <MonitoringEmptyState
              text={getEmptyFilterOptionsText(filterOptions)}
            />
          ) : !isDashboardContentVisible ? (
            <TotalFunnelSkeleton />
          ) : (
            <>
              <FiltersBar
                filterOptions={filterOptions}
                params={{
                  enteringClass,
                  compareTo,
                  cohort,
                  extend,
                  show,
                }}
                extendOptionsError={extendOptionsError}
                onRetryExtendOptions={refetchExtendOptions}
                onEnteringClassChange={handleEnteringClassChange}
                onCompareToChange={handleCompareToChange}
                onCohortChange={handleCohortChange}
                onExtendToggle={handleExtendToggle}
                onShowToggle={handleShowToggle}
              />

              {metricsQuery.isError && (
                <MonitoringEmptyState isError text={MONITORING_ERROR_TEXT} />
              )}

              {isMonitoringDataEmpty && (
                <MonitoringEmptyState text={NO_MONITORING_DATA_TEXT} />
              )}

              {!isMetricsUnavailable && (
                <>
                  <TotalFunnelSection
                    funnel={metricsQuery.data?.total}
                    enteringClass={enteringClass}
                    compareTo={compareTo}
                    displayMode={effectiveDisplayMode}
                    onDisplayModeChange={handleDisplayModeChange}
                    selectedUnentitledExtensions={selectedUnentitledExtensions}
                    skeletonCardCount={skeletonCardCount}
                    prospectAvailability={prospectAvailability}
                    isContentLoaded={hasContentLoaded}
                  />

                  <CriticalElementsRoster
                    criticalElements={criticalElementsQuery.data ?? []}
                    metrics={metricsQuery.data}
                    isMetricsLoading={metricsQuery.isFetching}
                    isRosterLoading={criticalElementsQuery.isLoading}
                    compareTo={compareTo}
                    displayMode={effectiveCeDisplayMode}
                    onDisplayModeChange={handleCeDisplayModeChange}
                    selectedUnentitledExtensions={selectedUnentitledExtensions}
                    skeletonCardCount={skeletonCardCount}
                    prospectAvailability={prospectAvailability}
                    isAnalyticsEnabled={isAnalyticsEnabled}
                    isContentLoaded={hasContentLoaded}
                    isError={criticalElementsQuery.isError}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </FeatureHintsProvider>
    </MarkSurfaceProvider>
  );
};

export default MonitoringDashboard;
